import type { PropType } from 'vue';
import type { TButtonProps } from './TButton.types';
import { Icon } from '@iconify/vue';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent } from 'vue';
import { useTheme } from '../../composables/theme';
import { useStyles } from './TButton.styles';

const defaultProps = {
  variant: 'primary',
  size: 'medium',
  type: 'button',
  color: 'primary',
};

export const TButton = defineComponent({
  name: 'TButton',
  props: {
    variant: String as PropType<TButtonProps['variant']>,
    size: String as PropType<TButtonProps['size']>,
    disabled: Boolean as PropType<TButtonProps['disabled']>,
    leftIcon: String as PropType<TButtonProps['leftIcon']>,
    rightIcon: String as PropType<TButtonProps['rightIcon']>,
    type: String as PropType<TButtonProps['type']>,
    fullWidth: Boolean as PropType<TButtonProps['fullWidth']>,
    loading: Boolean as PropType<TButtonProps['loading']>,
    color: String as PropType<TButtonProps['color']>,
  },
  emits: ['click'],
  setup(_props, { attrs, slots, emit }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));
    const color = computed(() => props.value.color);
    const baseClass = useStyles(color);
    const theme = useTheme();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.variant}`,
      `${baseClass.value}--${props.value.size}`,
      {
        [`${baseClass.value}--full-width`]: props.value.fullWidth,
        [`${baseClass.value}--loading`]: props.value.loading,
      },
    ]);

    const handleClick = () => {
      if (!props.value.loading) {
        emit('click');
      }
    };

    return () => (
      <button
        class={classes.value}
        type={props.value.type}
        disabled={props.value.disabled}
        onClick={handleClick}
        {...attrs}
      >
        {props.value.leftIcon && (
          <Icon icon={`${theme.value.iconSet}:${props.value.leftIcon}`} />
        )}
        {slots.default?.()}
        {props.value.rightIcon && (
          <Icon icon={`${theme.value.iconSet}:${props.value.rightIcon}`} />
        )}
        {props.value.loading && (
          <Icon
            class={`${baseClass.value}__spinner`}
            icon="svg-spinners:90-ring-with-bg"
          />
        )}
      </button>
    );
  },
});
